import { IBrandOffFaireQueryRewritingResponse } from '@faire/web-api--source/indigofair/data/IBrandOffFaireQueryRewritingResponse';
import { IBrandSearchQueryUXMessaging } from '@faire/web-api--source/indigofair/data/IBrandSearchQueryUXMessaging';
import { ILeanBrandTile } from '@faire/web-api--source/indigofair/data/ILeanBrandTile';
import { ILeanProductTile } from '@faire/web-api--source/indigofair/data/ILeanProductTile';
import { IMoreInC3 } from '@faire/web-api--source/indigofair/data/IMoreInC3';
import { IPaginationData } from '@faire/web-api--source/indigofair/data/IPaginationData';
import { IQueryProductsV2WithLeanTileResponse } from '@faire/web-api--source/indigofair/data/IQueryProductsV2WithLeanTileResponse';
import { IQuerySpellCheckResponse } from '@faire/web-api--source/indigofair/data/IQuerySpellCheckResponse';
import { IRetrievalQuery } from '@faire/web-api--source/indigofair/data/IRetrievalQuery';
import { ISearchRecommendation } from '@faire/web-api--source/indigofair/data/ISearchRecommendation';
import { ISuggestedC3Category } from '@faire/web-api--source/indigofair/data/ISuggestedC3Category';

export namespace IGetSearchProductsInfoResponse {
  export namespace IBrandWithProductTiles {
    /**
     * Return an instance of IBrandWithProductTiles with the provided fields. Any field not
     * listed will be populated with an empty value (an empty array,
     * or undefined, whichever is valid for the type of the value.)
     */
    export const build = (
      partial?: Partial<IBrandWithProductTiles>
    ): IBrandWithProductTiles =>
      ({
        product_tiles: [],
        ...(partial ?? {}),
      }) as IBrandWithProductTiles;
  }

  export interface IBrandWithProductTiles {
    brand_tile: ILeanProductTile.IBrand | undefined;
    product_tiles: Array<ILeanProductTile>;
  }

  /**
   * Return an instance of IGetSearchProductsInfoResponse with the provided fields. Any field not
   * listed will be populated with an empty value (an empty array,
   * or undefined, whichever is valid for the type of the value.)
   */
  export const build = (
    partial?: Partial<IGetSearchProductsInfoResponse>
  ): IGetSearchProductsInfoResponse =>
    ({
      product_tiles: [],
      brands_by_token: {},
      brand_tiles_related_to_search: [],
      related_search_terms: [],
      empty_suggestion_brands: [],
      brand_related_product_tiles: [],
      fallback_product_tiles: [],
      retrieval_queries: [],
      suggested_c3_categories: [],
      irrelevant_product_tiles: [],
      related_search_terms_pills: [],
      ...(partial ?? {}),
    }) as IGetSearchProductsInfoResponse;
}

export interface IGetSearchProductsInfoResponse {
  product_tiles: Array<ILeanProductTile>;
  brands_by_token: Partial<Record<string, ILeanProductTile.IBrand>>;
  /**
   * @deprecated
   */
  brand_tiles_related_to_search: Array<ILeanBrandTile>;
  pagination_data: IPaginationData | undefined;
  /**
   * Unique ID for the request. Must be supplied to /click-track endpoint
   * whenever a result from this response is clicked by the user.
   */
  request_id: string | undefined;
  related_search_terms: Array<string>;
  /**
   * When a query is about a brand name that we don't carry, sometimes we instead return products from brands
   * that our machine learning pipeline discovered as similar brands.
   * @deprecated
   */
  returning_related_brand_results: boolean | undefined;
  /**
   * When a query returns no results but contains more than 1 word, keep pruning words from the right of the
   * query and re-query Elastic Search until results are returned
   * @deprecated
   */
  returning_pruned_query_results: boolean | undefined;
  /**
   * When results are returned from a pruned query, save the pruned query
   */
  pruned_query: string | undefined;
  /**
   * TODO(FD-251000): clean this up (never populated)
   * @deprecated
   */
  empty_suggestion_brands: Array<ILeanBrandTile>;
  /**
   * When a query has less than 3 search results, embed the query via USE and look up products in Elastic search
   * @deprecated
   */
  returning_semantically_similar_results: boolean | undefined;
  category_name: string | undefined;
  category_url_id: string | undefined;
  suggested_brand_info: ILeanProductTile.IBrand | undefined;
  brand_related_product_tiles: Array<ILeanProductTile>;
  insider_membership_message: IBrandSearchQueryUXMessaging | undefined;
  /**
   * @deprecated
   */
  fallback_product_tiles: Array<ILeanProductTile>;
  /**
   * FE can request for this using returnRelatedExploreKeyword, and it will be populated with an explore page term
   * related to the search query if an explore page exists for it.
   */
  related_explore_keyword: string | undefined;
  /**
   * The issued Retrieval Queries for debugging
   */
  retrieval_queries: Array<IRetrievalQuery>;
  query_spell_check_response: IQuerySpellCheckResponse | undefined;
  suggested_c3_categories: Array<ISuggestedC3Category>;
  suggested_brand_cta_text: string | undefined;
  more_in_c3: IMoreInC3 | undefined;
  /**
   * Fields for exploring a new irrelevant products UX
   */
  irrelevant_product_tiles: Array<ILeanProductTile>;
  /**
   * TODO (quinn): This is an irrelevance classification, not a relevance classification. The name should be fixed
   */
  organic_relevance_classification:
    | keyof typeof IQueryProductsV2WithLeanTileResponse.OrganicRelevanceClassification
    | undefined;
  related_search_terms_pills: Array<ISearchRecommendation>;
  should_show_vertical_imagery: boolean | undefined;
  brand_off_faire_query_rewriting_response:
    | IBrandOffFaireQueryRewritingResponse
    | undefined;
}
